import { useCallback, useEffect, useState } from 'react';
import {
  tenantHasModule,
  type BeheerGebruikerRij,
  type GebruikWeekRij,
  type IntervisieGroup,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../api/client.js';
import { naamScore } from '../lib/naam-match.js';
import { useAuth } from '../auth/AuthProvider.js';

/**
 * Beheer Gebruikers (besluit Jos 3 sep 2026): per gebruiker het vinkje voor
 * Mila + Sara, en een deactiveerknop voor wie uit Entra is gehaald maar nooit
 * meer inlogde. Rollen en groepen blijven Entra. Aanvragers staan bovenaan.
 */
export function BeheerGebruikersPage(): JSX.Element {
  const { user } = useAuth();
  // RHOGO-beheerders (zorggroep_admin) beheren alleen hun eigen tenant; de
  // teamleider-markering en intervisie horen bij SUPPOHRT-modules en blijven
  // suppohrt_admin-werk (ADR 0018). De backend weigert ze ook.
  const isSuppohrtAdmin = user?.role === 'suppohrt_admin';
  const toonTeamleider = isSuppohrtAdmin && tenantHasModule(user?.tenantId, 'declaraties');
  const toonIntervisie = isSuppohrtAdmin && tenantHasModule(user?.tenantId, 'intervisie');
  const [rows, setRows] = useState<BeheerGebruikerRij[]>([]);
  const [groepen, setGroepen] = useState<IntervisieGroup[]>([]);
  const [weken, setWeken] = useState<GebruikWeekRij[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [bezigVoor, setBezigVoor] = useState<string | null>(null);
  const [zoek, setZoek] = useState('');
  // Inline naam-bewerking: id van de rij die open staat + de huidige invoer.
  const [naamBewerk, setNaamBewerk] = useState<{ id: string; waarde: string } | null>(null);

  const term = zoek.trim().toLowerCase();
  const zichtbaar = rows
    .filter((r) => !term || `${r.displayName} ${r.email}`.toLowerCase().includes(term))
    .sort((a, b) => {
      const aanvraag = Number(!!b.aiToolsRequestedAt) - Number(!!a.aiToolsRequestedAt);
      if (aanvraag !== 0) return aanvraag;
      return a.displayName.localeCompare(b.displayName, 'nl');
    });
  const aanvragen = rows.filter((r) => r.aiToolsRequestedAt && !r.aiTools).length;

  const load = useCallback((): void => {
    // Intervisiegroepen apart en zacht: een fout daar mag het gebruikersbeheer
    // niet blokkeren, dan ontbreekt alleen de kolom.
    if (toonIntervisie) {
      void apiGet<{ groups: IntervisieGroup[] }>('/api/v1/beheer/intervisie')
        .then((d) => setGroepen(d.groups))
        .catch(() => setGroepen([]));
    }
    void apiGet<{ weken: GebruikWeekRij[] }>('/api/v1/beheer/gebruikers/gebruik')
      .then((d) => setWeken(d.weken))
      .catch(() => setWeken([]));
    void apiGet<{ users: BeheerGebruikerRij[] }>('/api/v1/beheer/gebruikers')
      .then((d) => {
        setRows(d.users);
        setErrorMsg(null);
      })
      .catch((err) => {
        setErrorMsg(
          err instanceof ApiError
            ? `Gebruikers laden mislukt (${err.status}): ${err.message}`
            : 'Gebruikers laden mislukt.',
        );
      });
  }, [toonIntervisie]);

  useEffect(() => {
    load();
  }, [load]);

  async function doe(userId: string, actie: () => Promise<unknown>): Promise<void> {
    setBezigVoor(userId);
    setErrorMsg(null);
    try {
      await actie();
      load();
    } catch (err) {
      setErrorMsg(
        err instanceof ApiError ? err.message : 'Opslaan is mislukt. Probeer het opnieuw.',
      );
    } finally {
      setBezigVoor(null);
    }
  }

  function slaNaamOp(rij: BeheerGebruikerRij): void {
    if (!naamBewerk || naamBewerk.id !== rij.id) return;
    const nieuw = naamBewerk.waarde.trim();
    setNaamBewerk(null);
    // Ongewijzigd of gelijk aan de Entra-naam: niets schrijven.
    if (nieuw === rij.displayName || (nieuw === '' && rij.displayName === rij.entraDisplayName)) {
      return;
    }
    void doe(rij.id, () =>
      apiPut(`/api/v1/beheer/gebruikers/${rij.id}/weergavenaam`, {
        weergavenaam: nieuw === rij.entraDisplayName ? '' : nieuw,
      }),
    );
  }

  function deactiveer(rij: BeheerGebruikerRij): void {
    // ponytail: window.confirm i.p.v. een eigen dialoog; deactiveren is omkeerbaar
    // (opnieuw inloggen vanuit de Entra-groep activeert weer).
    if (!window.confirm(`${rij.displayName} deactiveren? Lopende sessies worden beëindigd.`))
      return;
    void doe(rij.id, () => apiPost(`/api/v1/beheer/gebruikers/${rij.id}/deactiveren`));
  }

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-5">
        <h1 className="text-xl font-bold text-ink-900">Beheer Gebruikers</h1>
        <p className="mt-1 text-[14px] text-ink-900/70">
          {toonTeamleider
            ? 'Zet Mila en Sara per gebruiker aan en markeer wie teamleider is (zelfde vinkje als in Beheer Declaraties).'
            : 'Zet Mila per gebruiker aan.'}{' '}
          Wie toegang aanvroeg staat bovenaan. Klik op een naam om de weergavenaam aan te passen
          (leeg = Entra-naam). Rollen en wie er überhaupt in kan, beheert IT in Entra; deactiveer
          hier alleen wie uit Entra is gehaald en niet meer inlogt.
        </p>
        <h2 className="mt-6 text-[15px] font-semibold text-ink-900">Gebruik per week</h2>
        <p className="mt-1 text-[13px] text-ink-900/60">
          Unieke gebruikers die de Kennishub die week minstens één keer openden. Telt sinds 26
          oktober 2026; daarvoor werd dit niet bijgehouden.
        </p>
        {weken.length === 0 ? (
          <p className="mt-2 text-[14px] text-ink-900/60">Nog geen gegevens.</p>
        ) : (
          <div className="mt-2 overflow-x-auto rounded-2xl border border-brand-100 bg-white">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="border-b border-brand-100 text-left text-[12px] uppercase tracking-wider text-ink-900/50">
                  <th className="px-4 py-2 font-semibold">Week</th>
                  <th className="px-4 py-2 font-semibold">Totaal</th>
                  <th className="px-4 py-2 font-semibold">Medewerkers</th>
                  <th className="px-4 py-2 font-semibold">Beheerders</th>
                  <th className="px-4 py-2 font-semibold">Overig</th>
                </tr>
              </thead>
              <tbody>
                {weken.map((w) => (
                  <tr key={w.week} className="border-b border-brand-100 last:border-0">
                    <td className="px-4 py-2 text-ink-900">{w.week.replace('-W', ' week ')}</td>
                    <td className="px-4 py-2 font-semibold text-ink-900">{w.totaal}</td>
                    <td className="px-4 py-2 text-ink-900/70">{w.medewerkers}</td>
                    <td className="px-4 py-2 text-ink-900/70">{w.beheerders}</td>
                    <td className="px-4 py-2 text-ink-900/70">{w.overig}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {aanvragen > 0 && (
          <p className="mt-3 rounded-lg bg-brand-50 px-4 py-3 text-[14px] text-brand-700">
            {aanvragen} openstaande {aanvragen === 1 ? 'aanvraag' : 'aanvragen'} voor Mila en Sara.
          </p>
        )}
        {errorMsg && (
          <p className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-[14px] text-red-700">{errorMsg}</p>
        )}
        <input
          type="search"
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
          placeholder="Zoek op naam of e-mail…"
          aria-label="Zoek gebruiker"
          className="mt-3 w-full max-w-sm rounded-lg border border-brand-100 bg-brand-50/50 px-3 py-2 text-[14px] text-ink-900 placeholder:text-ink-900/35 focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
        <p className="mt-2 text-[13px] text-ink-900/60">
          {zichtbaar.length} van {rows.length} gebruikers
        </p>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-brand-100 bg-white">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="border-b border-brand-100 text-left text-[12px] uppercase tracking-wider text-ink-900/50">
                <th className="px-4 py-2 font-semibold">Gebruiker</th>
                <th className="px-4 py-2 font-semibold">Rol</th>
                <th className="px-4 py-2 font-semibold">Status</th>
                <th className="px-4 py-2 font-semibold">Laatst actief</th>
                {toonTeamleider && <th className="px-4 py-2 font-semibold">Teamleider</th>}
                <th className="px-4 py-2 font-semibold">Mila + Sara</th>
                {toonIntervisie && <th className="px-4 py-2 font-semibold">Intervisie</th>}
                <th className="px-4 py-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {zichtbaar.map((rij) => (
                <tr key={rij.id} className="border-b border-brand-100/60 last:border-b-0">
                  <td className="px-4 py-2">
                    {naamBewerk?.id === rij.id ? (
                      <input
                        autoFocus
                        type="text"
                        maxLength={200}
                        value={naamBewerk.waarde}
                        aria-label={`Weergavenaam van ${rij.entraDisplayName}`}
                        placeholder={rij.entraDisplayName}
                        onChange={(e) => setNaamBewerk({ id: rij.id, waarde: e.target.value })}
                        onBlur={() => slaNaamOp(rij)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') slaNaamOp(rij);
                          if (e.key === 'Escape') setNaamBewerk(null);
                        }}
                        className="w-full max-w-xs rounded-lg border border-brand-400 bg-white px-2 py-1 text-[14px] text-ink-900 focus:outline-none focus:ring-1 focus:ring-brand-400"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setNaamBewerk({ id: rij.id, waarde: rij.displayName })}
                        disabled={bezigVoor === rij.id}
                        title="Weergavenaam aanpassen"
                        className="text-left text-ink-900 hover:underline disabled:opacity-50"
                      >
                        {rij.displayName}
                      </button>
                    )}
                    <div className="text-[12px] text-ink-900/60">
                      {rij.email}
                      {rij.displayName !== rij.entraDisplayName && (
                        <span className="ml-1 text-ink-900/40">
                          · Entra: {rij.entraDisplayName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-ink-900/70">
                    {rolLabel(rij.role, user?.tenantId)}
                    {rij.isHeadOffice && <span className="ml-1 text-ink-900/50">· kantoor</span>}
                  </td>
                  <td className="px-4 py-2 text-ink-900/70">
                    {rij.status === 'deactivated' ? 'Gedeactiveerd' : 'Actief'}
                  </td>
                  <td className="px-4 py-2 text-ink-900/70">{datum(rij.lastActiveAt)}</td>
                  {toonTeamleider && (
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        aria-label={`Teamleider: ${rij.displayName}`}
                        checked={rij.isTeamleider}
                        disabled={bezigVoor === rij.id || rij.status === 'deactivated'}
                        onChange={(e) =>
                          void doe(rij.id, () =>
                            apiPut(`/api/v1/beheer/gebruikers/${rij.id}/teamleider`, {
                              isTeamleider: e.target.checked,
                            }),
                          )
                        }
                        className="h-4 w-4 accent-brand-600 disabled:opacity-50"
                      />
                    </td>
                  )}
                  <td className="px-4 py-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        aria-label={`Mila en Sara aan voor ${rij.displayName}`}
                        checked={rij.aiTools}
                        disabled={bezigVoor === rij.id || rij.status === 'deactivated'}
                        onChange={(e) =>
                          void doe(rij.id, () =>
                            apiPut(`/api/v1/beheer/gebruikers/${rij.id}/ai-tools`, {
                              aiTools: e.target.checked,
                            }),
                          )
                        }
                        className="h-4 w-4 accent-brand-600 disabled:opacity-50"
                      />
                      {rij.aiToolsRequestedAt && !rij.aiTools && (
                        <span className="rounded-full bg-mosterdgeel-100 px-2 py-0.5 text-[12px] text-mosterdgeel-600">
                          aangevraagd {datum(rij.aiToolsRequestedAt)}
                        </span>
                      )}
                    </label>
                    {rij.aiTools && rij.aiToolsBy && (
                      <div className="mt-0.5 text-[12px] text-ink-900/55">
                        aangezet door {rij.aiToolsBy}
                        {rij.aiToolsAt && ` op ${datum(rij.aiToolsAt)}`}
                      </div>
                    )}
                  </td>
                  {toonIntervisie && (
                    <td className="px-4 py-2">
                      <IntervisieCel
                        rij={rij}
                        groepen={groepen}
                        bezig={bezigVoor === rij.id}
                        onToevoegen={(groepId, email) =>
                          void doe(rij.id, () =>
                            apiPost(`/api/v1/beheer/intervisie/${groepId}/leden`, {
                              userId: rij.id,
                              ...(email ? { email } : {}),
                            }),
                          )
                        }
                        onVerwijderen={(groepId) =>
                          void doe(rij.id, () =>
                            apiDelete(`/api/v1/beheer/intervisie/${groepId}/leden/${rij.id}`),
                          )
                        }
                      />
                    </td>
                  )}
                  <td className="px-4 py-2 text-right">
                    {rij.status !== 'deactivated' && rij.id !== user?.id && (
                      <button
                        type="button"
                        onClick={() => deactiveer(rij)}
                        disabled={bezigVoor === rij.id}
                        className="text-[13px] text-red-700 hover:underline disabled:opacity-50"
                      >
                        Deactiveren
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {term !== '' && zichtbaar.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-3 text-ink-900/60">
                    Geen gebruikers gevonden voor &quot;{zoek}&quot;.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

/**
 * Intervisiegroepen van één gebruiker, compact in een tabelkolom: groepsnamen
 * als tekst met een kruisje, één "+ groep"-link die pas een keuzelijst opent,
 * met bovenin de suggesties (TimeChimp-patroon). Een suggestie is een groep waar
 * een WACHTEND adres op naam bij deze gebruiker past ("staat als
 * virny.meijer@gmail.com"): klikken koppelt dat adres meteen aan dit account,
 * zodat de wachtlijst leegloopt. Zelfde route als Beheer > Intervisie.
 */
export function intervisieSuggesties(
  rij: { displayName: string; email: string },
  groepen: IntervisieGroup[],
): Array<{ groep: IntervisieGroup; email: string }> {
  const uit: Array<{ groep: IntervisieGroup; email: string; score: number }> = [];
  for (const groep of groepen) {
    for (const email of groep.pendingLedenEmails ?? []) {
      const score = naamScore(rij, { id: email, email, displayName: email.split('@')[0] ?? '' });
      // Minstens twee gedeelde naamdelen (voornaam én achternaam): één "anne"
      // is geen voorstel. Zelfde drempel als de keuzelijst in Beheer > Intervisie.
      if (score >= 4) uit.push({ groep, email, score });
    }
  }
  return uit.sort((a, b) => b.score - a.score).slice(0, 3);
}

/** "Intervisiegroep 6 Amsterdam" is in een tabelkolom te lang; het nummer plus regio volstaat. */
function korteGroepsnaam(naam: string): string {
  return naam.replace(/^Intervisie(groep)?\s+/i, '');
}

function IntervisieCel({
  rij,
  groepen,
  bezig,
  onToevoegen,
  onVerwijderen,
}: {
  rij: BeheerGebruikerRij;
  groepen: IntervisieGroup[];
  bezig: boolean;
  onToevoegen: (groepId: string, email?: string) => void;
  onVerwijderen: (groepId: string) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const lid = groepen.filter((g) => g.ledenUserIds.includes(rij.id));
  const overig = groepen.filter((g) => !g.ledenUserIds.includes(rij.id));
  const voorgesteld = intervisieSuggesties(rij, groepen).filter(
    (s) => !s.groep.ledenUserIds.includes(rij.id),
  );
  const kanBewerken = rij.status !== 'deactivated' && overig.length > 0;
  return (
    <div className="flex flex-col gap-0.5 text-[13px] leading-snug">
      {lid.map((g) => (
        <span key={g.id} className="group inline-flex items-center gap-1 whitespace-nowrap">
          {korteGroepsnaam(g.name)}
          <button
            type="button"
            aria-label={`${rij.displayName} uit ${g.name} halen`}
            title={`Uit ${g.name} halen`}
            disabled={bezig}
            onClick={() => onVerwijderen(g.id)}
            className="text-ink-900/25 hover:text-red-700 disabled:opacity-50"
          >
            ×
          </button>
        </span>
      ))}
      {kanBewerken && !open && (
        <button
          type="button"
          disabled={bezig}
          onClick={() => setOpen(true)}
          className="w-fit text-[12px] text-brand-600 hover:underline disabled:opacity-50"
        >
          + groep
        </button>
      )}
      {kanBewerken && open && (
        <select
          autoFocus
          value=""
          aria-label={`Intervisiegroep toevoegen voor ${rij.displayName}`}
          disabled={bezig}
          onBlur={() => setOpen(false)}
          onChange={(e) => {
            setOpen(false);
            const [groepId, email] = e.target.value.split('|');
            if (groepId) onToevoegen(groepId, email || undefined);
          }}
          className="w-44 rounded-lg border border-brand-100 bg-white px-2 py-1 text-[12px] text-ink-900 disabled:opacity-50"
        >
          <option value="">Kies een groep…</option>
          {voorgesteld.length > 0 && (
            <optgroup label="Waarschijnlijk deze">
              {voorgesteld.map((s) => (
                <option key={`${s.groep.id}|${s.email}`} value={`${s.groep.id}|${s.email}`}>
                  {korteGroepsnaam(s.groep.name)} · staat als {s.email}
                </option>
              ))}
            </optgroup>
          )}
          <optgroup label="Alle groepen">
            {overig.map((g) => (
              <option key={g.id} value={g.id}>
                {korteGroepsnaam(g.name)}
              </option>
            ))}
          </optgroup>
        </select>
      )}
    </div>
  );
}

function rolLabel(role: BeheerGebruikerRij['role'], tenantId: string | undefined): string {
  // In RHOGO zijn de zorggroep-rollen de eigen mensen, geen externen.
  const rhogo = tenantId === 'rhogo';
  switch (role) {
    case 'suppohrt_admin':
      return 'Beheerder';
    case 'suppohrt_medewerker':
      return 'POH-GGZ';
    case 'zorggroep_admin':
      return rhogo ? 'RHOGO Beheerder' : 'Extern beheer';
    case 'zorggroep_gebruiker':
      return rhogo ? 'RHOGO Medewerker' : 'Extern';
  }
}

function datum(iso: string | undefined): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
